import React from 'react';
import { TrendingUp, Shield, Target, BarChart3, Coins, Building2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import servicesImage from '../../assets/business-people-working-as-team-conference-room-plans-future_482257-25781.jpg_nur6h3.jpg';

const ServicesPage = () => {
  const services = [
    {
      icon: TrendingUp,
      title: 'Investment Management',
      description: 'COREVEST Investment Management service offers customized, sophisticated solutions designed to help you achieve your financial goals regardless of market conditions. Our seasoned team leverages deep market insight, advanced analytics, and a disciplined investment approach to build and manage portfolios tailored to your unique objectives and risk tolerance.',
      features: [
        'Blockchain-based Assets: Secure and grow wealth through digital asset investments.',
        'Real Estate: Capitalize on property market opportunities for stable returns.',
        'Oil and Gas: Explore energy sector investment opportunities for portfolio diversification.',
        'Stocks: Invest in high-potential stocks with thorough market analysis.'
      ]
    },
    {
      icon: BarChart3,
      title: 'Financial Planning',
      description: 'Our comprehensive financial planning services are designed to help you meet your long-term goals. From tailored advice to strategic planning, we ensure your financial future is secure and prosperous across multiple sectors.',
      features: [
        'Blockchain-based Assets: Plan for innovative digital asset allocation.',
        'Real Estate: Effective property investment strategies for sustained growth.',
        'Oil and Gas: Strategic energy sector planning for long-term growth.',
        'Stocks: Personalized stock investment plans for growth and income.'
      ]
    },
    {
      icon: Target,
      title: 'Market Analysis',
      description: 'COREVEST offers in-depth market analysis and insights across various sectors, helping you make informed decisions. We provide critical insights across various sectors, aiding in making informed investment decisions.',
      features: [
        'Blockchain-based Assets: Stay updated on digital asset trends and developments.',
        'Real Estate: Comprehensive analysis of real estate market trends.',
        'Oil and Gas: In-depth analysis of energy sector trends and commodity markets.',
        'Stocks: Detailed stock market analysis to guide investments.'
      ]
    },
    {
      icon: Shield,
      title: 'Portfolio Diversification',
      description: 'We help you diversify your investment portfolio to manage risk and enhance performance. Our services span across various sectors, ensuring a balanced and resilient investment approach.',
      features: [
        'Blockchain-based Assets: Include digital assets for high-growth potential.',
        'Real Estate: Diversify with real estate for long-term appreciation.',
        'Oil and Gas: Balance your portfolio with energy sector investments.',
        'Stocks: Combine stocks for a diversified, growth-oriented portfolio.'
      ]
    },
    {
      icon: Coins,
      title: 'Advisory Services',
      description: 'Our advisory services offer expert guidance tailored to your investment goals, with personalized insights and strategies across various sectors to help you succeed in your investments.',
      features: [
        'Blockchain-based Assets: Expert guidance on digital asset investments.',
        'Real Estate: Strategic advice for property investments.',
        'Oil and Gas: Expert recommendations on energy sector investment strategies.',
        'Stocks: Stock investment insights and recommendations.'
      ]
    },
    {
      icon: Building2,
      title: 'Wealth Management',
      description: 'COREVEST\'s wealth management services are designed to grow and protect your wealth. We integrate various sectors into a comprehensive wealth strategy to ensure your portfolio is resilient and strategically sound.',
      features: [
        'Blockchain-based Assets: Include digital assets for dynamic wealth growth.',
        'Real Estate: Grow wealth through strategic property investments.',
        'Oil and Gas: Balanced wealth management with energy sector investments.',
        'Stocks: Build wealth with a diversified stock portfolio.'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/80 to-navy-800/60"></div>
        <div className="absolute inset-0">
          <img
            src={servicesImage}
            alt="Services"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold font-serif mb-4">Services</h1>
          <p className="text-xl md:text-2xl text-gray-200">
            Home / <span className="text-gold-400">Services</span>
          </p>
        </div>
      </section>

      {/* Services Content */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-serif text-navy-900 mb-6">
              Our <span className="text-gold-600">Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              At COREVEST, we offer a diverse range of investment services tailored to help you achieve 
              financial growth and security. Our expertise spans across various sectors including 
              Blockchain-based Assets, Real Estate, Oil and Gas, and Stocks.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div key={index} className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                  <div className="flex items-start space-x-6">
                    <div className="flex-shrink-0">
                      <div className="inline-flex p-4 bg-gradient-to-r from-gold-500 to-gold-600 rounded-xl group-hover:scale-110 transition-transform duration-300 shadow-lg">
                        <IconComponent className="h-8 w-8 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-navy-900 mb-4">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-6">
                        {service.description}
                      </p>
                      <ul className="space-y-2">
                        {service.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start space-x-3">
                            <div className="flex-shrink-0 w-2 h-2 bg-gold-500 rounded-full mt-2"></div>
                            <span className="text-sm text-gray-600">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA Section */}
          <div className="mt-20 bg-gradient-to-r from-navy-900 to-navy-800 rounded-2xl p-12 text-center text-white">
            <h3 className="text-3xl md:text-4xl font-bold font-serif mb-6">
              Ready to Start Your Investment Journey?
            </h3>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Explore diverse opportunities, compare features, and get expert advice every step of the way. 
              Your ideal investment is just a click away!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/sectors/blockchain"
                className="inline-flex items-center justify-center px-8 py-4 bg-gold-500 hover:bg-gold-600 text-white font-bold rounded-full transition-colors duration-300"
              >
                Explore Sectors
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link 
                to="/plans/flexible"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-navy-900 font-bold rounded-full transition-all duration-300"
              >
                View Plans
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage; 